import { Fetcher } from '@this/src/util';

export type TicketCategory = 'changeable' | 'unchangeable';
export type PreferredTransportType = 'rail' | 'air';

export type ProposalItem = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
  flightName: string | null;
  seatClass: string | null;
  ticketType: string | null;
  ticketCategory: TicketCategory | null;
  price: number | null;
  priceWithTax: number | null;
  priceText: string | null;
  aiHighlightTags: string[];
  isAir: boolean | null;
  isShinkansen: boolean | null;
  selected: boolean;
};

export type ProposalBundle = {
  id: number;
  category: 'transport' | 'hotel' | 'package' | 'outward' | 'homeward';
  state: 'suggesting' | 'selected' | 'rejected';
  origin: string | null;
  destination: string | null;
  departureDate: string | null;
  preferredTransportType: PreferredTransportType | null;
  items: ProposalItem[];
};

type ProposalItemResponse = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departure_time: string | null;
  arrival_time: string | null;
  flight_name: string | null;
  seat_class: string | null;
  ticket_type: string | null;
  ticket_category: TicketCategory | null;
  price: number | null;
  price_with_tax: number | null;
  price_text: string | null;
  ai_highlight_tags: string[];
  is_air: boolean | null;
  is_shinkansen: boolean | null;
  selected: boolean;
};

type ProposalBundleResponse = {
  id: number;
  category: 'transport' | 'hotel' | 'package' | 'outward' | 'homeward';
  state: 'suggesting' | 'selected' | 'rejected';
  origin: string | null;
  destination: string | null;
  departure_date: string | null;
  preferred_transport_type: PreferredTransportType | null;
  items: ProposalItemResponse[];
};

type FetchProposalBundlesResponse = {
  bundles: ProposalBundleResponse[];
};

const toProposalItem = (item: ProposalItemResponse): ProposalItem => ({
  id: item.id,
  title: item.title,
  subtitle: item.subtitle,
  departureTime: item.departure_time,
  arrivalTime: item.arrival_time,
  flightName: item.flight_name,
  seatClass: item.seat_class,
  ticketType: item.ticket_type,
  ticketCategory: item.ticket_category,
  price: item.price,
  priceWithTax: item.price_with_tax,
  priceText: item.price_text,
  aiHighlightTags: item.ai_highlight_tags,
  isAir: item.is_air,
  isShinkansen: item.is_shinkansen,
  selected: item.selected
});

const toProposalBundle = (bundle: ProposalBundleResponse): ProposalBundle => ({
  id: bundle.id,
  category: bundle.category,
  state: bundle.state,
  origin: bundle.origin,
  destination: bundle.destination,
  departureDate: bundle.departure_date,
  preferredTransportType: bundle.preferred_transport_type,
  items: bundle.items.map(toProposalItem)
});

export const fetchProposalBundles = async (tripId: string): Promise<ProposalBundle[]> => {
  const response = await Fetcher.get<FetchProposalBundlesResponse>(`/trips/${tripId}/ai_proposal_bundles.json`);
  return response.bundles.map(toProposalBundle);
};

type RejectProposalBundleResponse = {
  success: boolean;
  bundle_id: number;
  state: string;
};

export const rejectProposalBundle = async (tripId: string, bundleId: number): Promise<{ success: boolean }> => {
  const response = await Fetcher.patch<RejectProposalBundleResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/reject.json`
  );
  return { success: response.success };
};

type SelectProposalItemResponse = {
  success: boolean;
  bundle_id: number;
  item_id: number;
  state: 'suggesting' | 'selected' | 'rejected';
};

export const selectProposalItem = async (
  tripId: string,
  bundleId: number,
  itemId: number
): Promise<{ success: boolean; state: ProposalBundle['state'] }> => {
  const response = await Fetcher.patch<SelectProposalItemResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/items/${itemId}/select.json`
  );
  return { success: response.success, state: response.state };
};

type SummaryItemResponse = {
  category: 'outward' | 'homeward' | 'hotel';
  label: string;
  text: string;
  price: number | null;
  price_with_tax: number | null;
};

type SummaryResponse = {
  items: SummaryItemResponse[];
  total_price: number;
  total_price_with_tax: number;
  has_selection: boolean;
};

type CompleteProposalSelectionResponse = {
  success: boolean;
  message_id: number;
  summary: SummaryResponse | null;
  has_selection: boolean;
};

export const completeProposalSelection = async (tripId: string): Promise<{ success: boolean }> => {
  const response = await Fetcher.post<CompleteProposalSelectionResponse>(
    `/trips/${tripId}/ai_proposal_bundles/complete.json`,
    {}
  );
  return { success: response.success };
};
